import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../lib/api';
import { Lock, CreditCard } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Wraps article content. Calls the /paywall/check/{id} endpoint and either
 * renders the full content (children) or a soft paywall preview when the
 * caller has exhausted their metered allowance / lacks a premium tier.
 */
export default function PaywallGate({ articleId, isPremiumArticle, children }) {
  const [state, setState] = useState({ loading: true, can_read: true, reason: null, used: 0, limit: -1, tier: 'free' });

  useEffect(() => {
    if (!articleId) return;
    let cancelled = false;
    api
      .get(`/api/paywall/check/${articleId}`)
      .then((r) => {
        if (!cancelled) setState({ loading: false, ...r.data });
      })
      .catch(() => {
        if (!cancelled) setState((s) => ({ ...s, loading: false }));
      });
    return () => {
      cancelled = true;
    };
  }, [articleId]);

  if (state.loading || state.can_read) return <>{children}</>;

  return (
    <div data-testid="paywall-gate" className="my-8">
      {/* Render a fade-out preview of the content */}
      <div className="relative max-h-[260px] overflow-hidden">
        {children}
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#FBF7EF] via-[#FBF7EF]/85 to-transparent" />
      </div>

      <div className="mt-4 rounded-2xl border border-[#E7E0D6] bg-white p-6 text-center shadow-sm">
        <div className="mx-auto w-12 h-12 rounded-full bg-[#B89B5E]/15 text-[#B89B5E] flex items-center justify-center mb-3">
          <Lock className="w-5 h-5" />
        </div>
        <h2 className="news-masthead text-xl font-bold mb-1">
          {state.reason === 'premium_only' ? 'Premium story' : 'You’ve hit this month’s free limit'}
        </h2>
        <p className="text-sm text-[#5B616E] max-w-md mx-auto">
          {state.reason === 'premium_only'
            ? 'Subscribe to Premium to keep reading exclusive analysis and original reporting.'
            : `You’ve read ${state.used}${state.limit > 0 ? ` of ${state.limit}` : ''} free articles this month. Subscribe for unlimited access.`}
        </p>
        <div className="mt-4 flex items-center justify-center gap-2">
          <Link to="/subscription">
            <Button className="bg-[#121316] text-white hover:bg-[#1B1D22]">
              <CreditCard className="w-4 h-4 mr-2" />
              See plans
            </Button>
          </Link>
          {state.tier === 'free' && (
            <Link to="/login" className="text-sm text-[#1E5A8A] underline">
              Already subscribed? Sign in
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
